import clsx from 'clsx'
import forEach from 'lodash/forEach'
import includes from 'lodash/includes'
import uniqBy from 'lodash/uniqBy'
import React, { CSSProperties, useContext, useMemo } from 'react'

import { BlockTypeWithGlobal, SplitTestGroup } from '@Generated/graphql'
import { BlockContext, useBlockConfig } from '@Globals/CMS/Context'
import { useBlockComponents } from '@Globals/CMS/useBlockComponents'
import { AnchorItemInjection, GridItemInjection, InjectionSplitTest } from '@Globals/Injections/Types'
import { MetadataContext } from '@Globals/Metadata'
import { Experiment } from '@Globals/SplitTests/Experiment'
import { Test } from '@Globals/SplitTests/types'

import styles from './injections.module.scss'

/**
 * The following css must be defined in the parent element for all supported column counts
 * > ${StyledGridInjection} {
 *   --gi-<column>-display: none;
 * }
 *
 * To following css must be defined in the parent elemement, for each breakpoint
 * > ${StyledGridInjection} {
 *   --gi-display: var(--gi-<column>-display);
 *   --gi-height: var(--gi-<column>-height);
 *   --gi-width: var(--gi-<column>-width);
 *   --gi-x: var(--gi-<column>-x);
 *   --gi-y: var(--gi-<column>-y);
 *   --gi-additional-height: <additional height for a 1x1 tile>;
 *   --gi-column-gap: <grid column gap, if any>;
 *   --gi-row-gap: <grid row gap, if any>;
 * }
 */
function getStyles(dimensions: GridItemInjection['dimensions']) {
  const result: CSSProperties = {}

  forEach(dimensions, (item) => {
    if (!item
      || !item.columns
      || !item.height
      || !item.width
      || !item.x
      || !item.y
    ) {
      return
    }

    result[`--gi-${item.columns}-display`] = 'block'
    result[`--gi-${item.columns}-height`] = item.height
    result[`--gi-${item.columns}-width`] = item.width
    result[`--gi-${item.columns}-x`] = item.x
    result[`--gi-${item.columns}-y`] = item.y
  })

  return result
}

const Content = ({
  blocks,
  className,
  idPrefix,
}: {
  blocks: GridItemInjection['blocks']
  className?: string
  idPrefix: string
}) => {
  const components = useBlockComponents((blocks || []) as BlockTypeWithGlobal[], idPrefix, true, undefined, true)

  if (components && components.length !== 0) {
    return (
      <div className={className}>{components}</div>
    )
  }

  return null
}

export const AnchorItemInjections = ({ anchor }: { anchor: string }) => {
  const {
    blockID,
    blockType,
  } = useContext(BlockContext)
  const { anchorInjections } = useBlockConfig()
  const items = useMemo(() => {
    const result: AnchorItemInjection[] = []

    forEach(anchorInjections, (injection) => {
      // TODO: think about split testing, maybe it was set up on the wrong level, and should be moved to each injection instead
      forEach(injection.items, (item) => {
        if (item.__typename === 'InjectionTypeAnchor') {
          if (!item.id || !item.anchor || item.anchor !== anchor) {
            return
          }

          result.push(item)
          return
        }
      })
    })

    return uniqBy(result, 'id')
  }, [anchor, anchorInjections])

  if (!items || items.length === 0) {
    return null
  }

  return (
    <>
      {items.map(item => (
        <Content key={item.id!} blocks={item.blocks} className={styles.injectionsWrapper} idPrefix={`${blockType}-${blockID}-ai-${item.id}`} />
      ))}
    </>
  )
}

export const GlobalAnchorItemInjections = ({ anchor }: { anchor: string }) => {
  const { globalAnchorInjections } = useContext(MetadataContext)
  const items = useMemo(() => {
    const result: AnchorItemInjection[] = []

    forEach(globalAnchorInjections?.[anchor], (injection) => {
      // TODO: think about split testing, maybe it was set up on the wrong level, and should be moved to each injection instead
      forEach(injection.items, (item) => {
        if (item.__typename === 'InjectionTypeAnchor') {
          if (!item.id || !item.anchor || item.anchor !== anchor) {
            return
          }

          result.push(item)
          return
        }
      })
    })

    return uniqBy(result, 'id')
  }, [anchor, globalAnchorInjections])

  if (!items || items.length === 0) {
    return null
  }

  return (
    <>
      {items.map(item => (
        <Content key={item.id!} blocks={item.blocks} className={styles.injectionsWrapper} idPrefix={`global-ai-${item.id}`} />
      ))}
    </>
  )
}

export const GridItemInjections = () => {
  const {
    blockID,
    blockType,
  } = useContext(BlockContext)
  const { anchorInjections } = useBlockConfig()
  const items = useMemo(() => {
    const result: {
      item: GridItemInjection
      splitTest?: InjectionSplitTest | null
    }[] = []

    forEach(anchorInjections, (injection) => {
      // TODO: think about split testing, maybe it was set up on the wrong level, and should be moved to each injection instead
      forEach(injection.items, (item) => {
        if (item.__typename === 'InjectionTypeGrid') {
          if (!item.id) {
            return
          }

          result.push({
            item,
            splitTest: injection.splitTest,
          })
          return
        }
      })
    })

    return uniqBy(result, 'item.id')
  }, [anchorInjections])

  if (!items || items.length === 0) {
    return null
  }

  return (
    <>
      {items.map((injection) => {
        let content = (
          <div
            key={injection.item.id!}
            className={clsx(styles.gridInjection, 'gridInjection')}
            id={`${blockType}-${blockID}-gi-${injection.item.id!}`}
            style={getStyles(injection.item.dimensions)}
          >
            <Content blocks={injection.item.blocks} idPrefix={`${blockType}-${blockID}`} />
          </div>
        )

        if (injection?.splitTest?.test) {
          /* eslint-disable react/jsx-no-useless-fragment */
          content = (
            <Experiment
              key={injection.item.id!}
              a={includes(injection?.splitTest.groups, SplitTestGroup.A) ? <>{content}</> : undefined}
              b={includes(injection?.splitTest.groups, SplitTestGroup.B) ? <>{content}</> : undefined}
              c={includes(injection?.splitTest.groups, SplitTestGroup.C) ? <>{content}</> : undefined}
              d={includes(injection?.splitTest.groups, SplitTestGroup.D) ? <>{content}</> : undefined}
              test={injection?.splitTest.test as Test}
            />
          )
          /* eslint-enable react/jsx-no-useless-fragment */
        }

        return content
      })}
    </>
  )
}
